import { Config } from '@/types/config';
import '@/utils/public-path';
import '@/utils/polyfills';
import '@/utils/globals';

import { createConfig } from '@/utils/plugins/config';
import { applyConfigSet } from '@/utils/config-sets';
import { makeRequest, getSession } from '@/utils/request';
import { setStyles } from '@/utils/customizing';
import insertScript from '@/utils/insert-script';
import insertStyle from '@/utils/insert-style';
import insertPreload from '@/utils/insert-preload';
import { insertTrackerIframe } from '@/utils/analytics';

import '@/scss/blocks/chat-opener.scss';
import '@/scss/blocks/chat-window.scss';
import '@/scss/blocks/search-widget.scss';
import '@/scss/blocks/linear-progress.scss';

import normalModeTpl from '@/templates/normal-mode.tpl';
import searchModeTpl from '@/templates/search-mode.tpl';
import chatModeTpl from '@/templates/chat-mode.tpl';
import inlineModeTpl from '@/templates/inline-mode.tpl';

import { getAddParams } from '@/utils/parse-get-params';
import { InitialRequest } from '@/types/requests';

let isDev = process.env.NODE_ENV === 'development';

(function () {
    if (window.__odrLoaderStarted) {
        return;
    }

    window.__odrLoaderStarted = true;
    window.__odrAllowInitGa = true;
    window.__odrAllowInitAudit = true;

    if (!window.OA) {
        window.OA = [];
        let odoroScripts = document.querySelectorAll('[data-odoro="script"]');

        if (odoroScripts.length) {
            [].slice.call(odoroScripts).forEach(function (odoroScript: HTMLElement) {
                // eslint-disable-next-line no-eval
                (window.OA as Array<Partial<Config>>).push(eval('(function () { return ' + odoroScript.innerHTML.trim() + ' })()'));
            });
        }
    }

    function init (options: Partial<Config>, index: number) {
        options.addParams = getAddParams(window.location.search);
        let appConfig = createConfig(options, index);

        // Only init the tracking GA iframe for account turn on sendInternalEvents props
        if (appConfig.sendInternalEvents && !window.__odrGaStarted) {
            insertTrackerIframe({
                appConfig,
                callback: () => {
                    window.__odrGaLoaded = true;
                },
            });
            window.__odrGaStarted = true;
        }

        let usedModes = ['normal', 'chat', 'search', 'inline'].filter(function (mode) {
            let modes = window.OA instanceof Array ? window.OA.map((config) => config.mode || 'normal') : [window.OA.mode || 'normal'];
            return !!(modes.indexOf(mode as Config['mode']) > -1);
        }).join('~');

        let jsResource = `${appConfig.staticUrl}/js/entry-${usedModes}.js`;
        let cssResource = `${appConfig.staticUrl}/css/entry-${usedModes}.css`;

        window.__odrConfig.push(appConfig);

        try {
            let set = JSON.parse(window.localStorage.getItem('odrConfigSet') as string);
            if (set) {
                applyConfigSet(appConfig, set);
            }
        } catch (ignore) {}

        appConfig.loadHook(function () {
            function getContainer () {
                let container;

                if (appConfig.mode === 'chat' && !appConfig.container) {
                    return window.document.body;
                } else {
                    container = typeof appConfig.container === 'string' ? document.querySelector(appConfig.container) : appConfig.container;
                    if (!container) {
                        throw new Error('Could not find container element');
                    }
                    return container;
                }
            }

            function loadVue ({ appConfig, index, callback }: { appConfig: Config; index: number; callback?: Function}) {
                let stylesLoaded = false;
                let jsLoaded = !!(window.__odrInit && window.__odrInit[appConfig.mode] instanceof Function);

                function checkInit () {
                    if (jsLoaded && stylesLoaded) {
                        (window.__odrInit[appConfig.mode] as Function)(appConfig, index);
                        if (callback) {
                            callback();
                        }

                        if (appConfig.mode !== 'search') {
                            insertPreload({ src: `${appConfig.staticUrl}/js/providerslist.js`, as: 'script', pass: isDev });
                            insertPreload({ src: `${appConfig.staticUrl}/css/providerslist.css`, as: 'style', pass: isDev });
                        }
                    }
                }

                insertStyle({
                    src: cssResource,
                    callback: function () {
                        stylesLoaded = true;
                        checkInit();
                    },
                    pass: isDev,
                });

                if (!jsLoaded) {
                    insertScript({
                        src: jsResource,
                        callback: function () {
                            jsLoaded = true;
                            checkInit();
                        },
                        pass: isDev,
                    });
                } else {
                    checkInit();
                }
            }

            window.__odrChatVars = {
                loaded: false,
                opened: appConfig.chatOpenedByDefault,
            };

            if (appConfig.mode === 'chat' && !appConfig.container) {
                insertStyle({
                    src: `${appConfig.staticUrl}/css/index.css`,
                    pass: isDev,
                    callback: function () {
                        insertPreload({ src: jsResource, as: 'script', pass: isDev });
                        insertPreload({ src: cssResource, as: 'style', pass: isDev });

                        let span = document.createElement('span');
                        span.innerHTML = chatModeTpl({ index, appConfig });
                        getContainer().appendChild(span);
                        setStyles(index, appConfig);

                        if (appConfig.chatHideOpenButton) {
                            loadVue({
                                appConfig,
                                index,
                                callback () { window.__odrChatVars.loaded = true },
                            })
                        } else {
                            let opener = document.querySelector<HTMLElement>(`[data-odr-opener="${index}"]`);
                            let openerTitle = document.querySelector<HTMLElement>(`[data-odr-opener-title="${index}"]`);
                            let openChat = () => {
                                if (!window.__odrChatVars.loaded) {
                                    window.__odrChatVars.opened = true;
                                    if (openerTitle) {
                                        openerTitle.innerHTML = 'Please wait, Loading...';
                                    }
                                    loadVue({
                                        appConfig,
                                        index,
                                        callback () {
                                            window.__odrChatVars.loaded = true;
                                            if (openerTitle) {
                                                openerTitle.innerHTML = appConfig.chatButtonHeader;
                                            }
                                        },
                                    });
                                } else {
                                    window.__odrChatVars.opened = true;
                                }
                            };
                            if (opener !== null) {
                                opener.addEventListener('click', openChat);
                                if (window.__odrChatVars.opened) {
                                    openChat();
                                }
                            }
                        }
                    },
                });
            } else if (appConfig.mode === 'search') {
                window.__odr.requestPrefetch[index] = new Promise((resolve) => {
                    getSession(appConfig, index, (_, step) => {
                        if (step) {
                            resolve(step);
                        }
                    });
                });
                insertPreload({ src: jsResource, as: 'script', pass: isDev });
                insertPreload({ src: cssResource, as: 'style', pass: isDev });

                insertStyle({
                    src: `${appConfig.staticUrl}/css/index.css`,
                    pass: isDev,
                    callback: function () {
                        getContainer().innerHTML = searchModeTpl({ index, appConfig });
                        setStyles(index, appConfig);

                        let searchEl = document.querySelector(`[data-odr-search="${index}"]`) as HTMLInputElement;
                        if (searchEl) {
                            if (window.navigator.userAgent.match(/(iPhone|iPad)/i)) { // mobile safari blocks focus() methjod on spawned widget
                                loadVue({ appConfig, index });
                            } else {
                                searchEl.addEventListener('focus', function () {
                                    loadVue({ appConfig, index });
                                });
                            }

                            searchEl.addEventListener('keyup', function () {
                                window.__odrSearchVars.value = searchEl.value;
                            });
                        }
                    },
                });
            } else if (['normal', 'inline'].indexOf(appConfig.mode) !== -1 || (appConfig.mode === 'chat' && appConfig.container)) {
                window.__odr.requestPrefetch[index] = new Promise((resolve, reject) => {
                    appConfig.beforeInitialRequest(() => {
                        makeRequest({
                            data: new InitialRequest({}, appConfig),
                            appConfig,
                            callback: function (status, step, _) {
                                if (status === 200 && step !== null) {
                                    resolve(step);
                                } else {
                                    reject(status);
                                }
                            },
                        });
                    }, appConfig);
                });

                getContainer().innerHTML = appConfig.mode === 'normal' ? normalModeTpl({ index }) : inlineModeTpl({ index });
                setStyles(index, appConfig);
                loadVue({ appConfig, index });
            }
        });
    }

    if (window.OA instanceof Array) {
        window.OA.forEach(function (config, index) {
            init(config, index);
        });
    } else if (window.OA instanceof Object) {
        init(window.OA, 0);
    }
})();
